import { useState } from 'react'
import { toast } from 'sonner'
import { useGetAllInstructionsQuery } from '@/features/admin/adminApi'
import { useDeleteInstructionMutation } from '@/features/instructions/instructionsApi'
import { DeleteInstructionDialog } from '@/features/instructions/components/DeleteInstructionDialog'
import type { Instruction } from '@/features/instructions/types'
import { errorMessage } from '@/features/instructions/lib/errorMessage'
import { AdminPagination } from './AdminPagination'
import { InstructionRow } from './InstructionRow'
import { useMessages } from '@/i18n'
import { messages } from './messages'

const LIMIT = 20

export const AdminInstructionsTable = () => {
  const [page, setPage] = useState(1)
  const { data, isLoading, refetch } = useGetAllInstructionsQuery({ page, limit: LIMIT })
  const [deleteInstruction] = useDeleteInstructionMutation()
  const [deleteTarget, setDeleteTarget] = useState<Instruction | null>(null)
  const m = useMessages(messages)

  const handleDelete = async () => {
    if (!deleteTarget) return
    try {
      await deleteInstruction(deleteTarget.id).unwrap()
      toast.success(m.deleted)
      refetch()
    } catch (err) {
      toast.error(errorMessage(err))
    } finally {
      setDeleteTarget(null)
    }
  }

  if (isLoading) {
    return <div className="text-sm text-muted-foreground py-8 text-center">{m.loading}</div>
  }

  if (!data || data.items.length === 0) {
    return <div className="text-sm text-muted-foreground py-8 text-center">{m.noArticles}</div>
  }

  return (
    <div>
      <div className="border border-border rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border bg-muted/50">
              <th className="text-left px-4 py-2.5 font-medium text-muted-foreground">
                {m.colName}
              </th>
              <th className="text-left px-4 py-2.5 font-medium text-muted-foreground">
                {m.colAuthor}
              </th>
              <th className="text-left px-4 py-2.5 font-medium text-muted-foreground">
                {m.colAccess}
              </th>
              <th className="text-left px-4 py-2.5 font-medium text-muted-foreground">
                {m.colUpdated}
              </th>
              <th className="w-12" />
            </tr>
          </thead>
          <tbody>
            {data.items.map((instruction) => (
              <InstructionRow
                key={instruction.id}
                instruction={instruction}
                onDelete={setDeleteTarget}
              />
            ))}
          </tbody>
        </table>
      </div>
      <AdminPagination page={data.page} pages={data.pages} onPageChange={setPage} />
      {deleteTarget ? (
        <DeleteInstructionDialog
          open
          title={deleteTarget.title}
          onOpenChange={(open) => {
            if (!open) setDeleteTarget(null)
          }}
          onConfirm={handleDelete}
        />
      ) : null}
    </div>
  )
}
